<template>
  <div>
    <div class="filter-box">
      <div class="filter">
        <div class="search-box">
          <label class="field-label">{{ $t('transactions.filter.search') }}:</label>
          <div class="search">
            <div class="search-input-box">
              <input type="text" v-model="searchInput" :disabled="isLoading"
                v-on:keypress.enter="search" :class="{'has-error':!!hasError.searchMsg}"
                v-on:blur="blurSearch" ref="search" v-on:keypress="isNumber"/>
                <transition name="fade">
                  <i class="far fa-times-circle fa-lg" @click="clearSearch"
                    v-show="searchInput.length > 0"></i>
                </transition>
            </div>
            <button :class="{'button xsmall success':true, 'disabled':isLoading}"
              @click="search">
              <i class="fas fa-search fa-lg"></i>
            </button>
          </div>

          <transition name="slide-up">
            <div class="error-msg" v-show="!!hasError.searchMsg">
              {{ hasError.searchMsg }}
            </div>
          </transition>
        </div>

        <div class="status" v-if="!isMobile">
          <label class="field-label">{{ $t('transactions.filter.status') }}:</label>
          <select v-model="status" :disabled="isLoading">
            <option value="">{{ $t('transactions.filter.all') }}</option>
            <option value="Declined">{{ $t('transactions.filter.declined') }}</option>
            <option value="Cancelled">{{ $t('transactions.filter.cancelled') }}</option>
            <option value="Purchased">{{ $t('transactions.filter.purchased') }}</option>
            <option value="Promo">{{ $t('transactions.filter.promo') }}</option>
            <option value="Authorized">{{ $t('transactions.filter.authorized') }}</option>
            <option value="No Show">{{ $t('transactions.filter.noShow') }}</option>
            <option value="Completed">{{ $t('transactions.filter.completed') }}</option>
            <option value="Refund">{{ $t('transactions.filter.refund') }}</option>
            <option value="Edited">{{ $t('transactions.filter.edited') }}</option>
          </select>
        </div>

        <div class="brand">
          <div class="status">
            <label class="field-label">Brand:</label>
            <select v-model="brand" :disabled="isLoading || brands.loading">
              <option value="">All Brands</option>
              <option v-for="(br, i) in brands.list" :value="br.id" :key="i">{{ brandLabel(br) }}</option>
            </select>
          </div>
        </div>

        <div class="date-range">
          <label class="field-label">{{ $t('transactions.filter.dateRange') }}:</label>
          <select v-model="dateType" :disabled="isLoading">
            <option value="all">{{ $t('transactions.filter.all') }}</option>
            <option value="custom">{{ $t('transactions.filter.custom') }}</option>
            <option value="withinDay">{{ $t('transactions.filter.withinDay') }}</option>
            <option value="monthToDate">{{ $t('transactions.filter.monthToDate') }}</option>
            <option value="lastMonth">{{ $t('transactions.filter.lastMonth') }}</option>
            <option value="lastWeek">{{ $t('transactions.filter.lastWeek') }}</option>
            <option value="last2Week">{{ $t('transactions.filter.last2Week') }}</option>
            <option value="last30Days">{{ $t('transactions.filter.last30Days') }}</option>
            <option value="last60Days">{{ $t('transactions.filter.last60Days') }}</option>
            <option value="last90Days">{{ $t('transactions.filter.last90Days') }}</option>
          </select>

          <div class="datepicker-box">
            <datepicker :format="calendarFormat" class="datepicker inline"
              v-model="date.from" v-on:input="changeDate('from')"
              :disabled-picker="isLoading" :input-class="{'datepicker-empty':hasError.from}"
              :placeholder="$t('transactions.filter.from')"></datepicker>
          </div>

          <div class="datepicker-box">
            <datepicker :format="calendarFormat" class="datepicker inline"
              v-model="date.to" v-on:input="changeDate('to')"
              :disabled-picker="isLoading" :input-class="{'datepicker-empty':hasError.to}"
              :placeholder="$t('transactions.filter.to')"></datepicker>
          </div>

          <transition name="slide-up">
            <div class="error-msg" v-show="!!hasError.fromToMsg">
              {{ hasError.fromToMsg }}
            </div>
          </transition>
        </div>

        <div class="extra">
          <div class="status" v-if="isMobile">
            <label class="field-label">{{ $t('transactions.filter.status') }}:</label>
            <select v-model="status" :disabled="isLoading">
              <option value="">{{ $t('transactions.filter.all') }}</option>
              <option value="Declined">{{ $t('transactions.filter.declined') }}</option>
              <option value="Cancelled">{{ $t('transactions.filter.cancelled') }}</option>
              <option value="Purchased">{{ $t('transactions.filter.purchased') }}</option>
              <option value="Promo">{{ $t('transactions.filter.promo') }}</option>
              <option value="Authorized">{{ $t('transactions.filter.authorized') }}</option>
              <option value="No Show">{{ $t('transactions.filter.noShow') }}</option>
              <option value="Completed">{{ $t('transactions.filter.completed') }}</option>
              <option value="Refund">{{ $t('transactions.filter.refund') }}</option>
              <option value="Edited">{{ $t('transactions.filter.edited') }}</option>
            </select>
          </div>
          <a :class="{'button cta':true, 'disabled':!isListEmpty || isLoading}"
            @click="refresh">
            <i :class="{'fas fa-sync-alt':true, 'fa-spin':refreshLoading}"></i>
            {{ $t('transactions.filter.refresh') }}
          </a>
        </div>
      </div>

      <div class="export">
        <a :class="{'button cta success':true, 'disabled':!isListEmpty || isLoading}"
          @click="exportTrx">{{ $t('transactions.filter.export') }}</a>
      </div>
    </div>
  </div>
</template>

<script>
/* global CONFIG */
import moment from 'moment-timezone'
import datepicker from 'vuejs-datepicker'
import ApiTransactions from '@/modules/api/transactions'
import ApiBrands from '@/modules/api/brands'
import CsvExport from 'csvexport'

const exporter = CsvExport.create({
  filename: "export.csv",
  columns: [
    'Date', 'Booking Time', 'Pickup Time', 'Service Area', 'Prov ID',
    'Prov License', 'Company', 'RSVP ID', 'Invoice ID', 'Service Type',
    'First Name', 'Last Name', 'Credit Card', 'Email', 'Mobile', 'Source',
    'Final Offer', 'Charge Amount', 'Refunded Amount', 'Promo Used', 'Driver Payment',
    'Bonus', 'Cancel Fees', 'Status', 'Pickup Location', 'Dropoff Location'
  ]
});

export default {
  name: 'transactions-filter',
  components: { datepicker },
  props: [ 'isMobile' ],
  data () {
    return {
      searchInput: '',
      blurDelay: '',
      hasError: {
        searchMsg: '',
        from: false,
        to: false,
        fromToMsg: ''
      },
      refreshLoading: false
    }
  },
  computed: {
    status:{
      get: function () { return this.$store.state.transactions.filter.status },
      set: function (value) {
        this.$store.commit('transactions/setStatus', value)
        if ((this.date.from === '' || this.date.to === '') && this.dateType !== 'all') {
          this.hasError.from = this.date.from === ''
          this.hasError.to = this.date.to === ''
          return
        }
        this.getTrasactions(false)
      }
    },
    dateType: {
      get: function () { return this.$store.state.transactions.filter.date },
      set: function (value) {
        this.$store.commit('transactions/setDate', value)

        let from = ''
        let to = value === 'all' ? '' : moment().format('MM/DD/YYYY')
        if (value === 'withinDay') {
          from = moment().format('MM/DD/YYYY')
        }
        if (value === 'monthToDate') {
          from = moment(moment().format('MM') + '/1/' + moment().format('YYYY'), 'MM/DD/YYYY').format('MM/DD/YYYY')
        }
        if (value === 'lastMonth') {
          let lastMonth = moment().subtract(1, 'months')
          from = lastMonth.format('MM/01/YYYY')
          to = lastMonth.endOf('month').format('MM/DD/YYYY')
        }
        if (value === 'lastWeek') {
          let subtractDays = 0
          let currentDate = parseInt(moment().format('E'))
          if (currentDate !== 7) { subtractDays = currentDate }

          from = moment().subtract(subtractDays + 6, 'days').format('MM/DD/YYYY')
          to = moment().subtract(subtractDays, 'days').format('MM/DD/YYYY')
        }
        if (value === 'last2Week') {
          let subtractDays = 0
          let currentDate = parseInt(moment().format('E'))
          if (currentDate !== 7) { subtractDays = currentDate }
          from = moment().subtract(subtractDays + 13, 'days').format('MM/DD/YYYY')
          to = moment().subtract(subtractDays, 'days').format('MM/DD/YYYY')
        }
        if (value === 'last30Days') {
          from = moment().subtract(30, 'days').format('MM/DD/YYYY')
        }
        if (value === 'last60Days') {
          from = moment().subtract(60, 'days').format('MM/DD/YYYY')
        }
        if (value === 'last90Days') {
          from = moment().subtract(90, 'days').format('MM/DD/YYYY')
        }

        if (value !== 'custom') {
          this.hasError = { searchMsg: '', from: false, to: false, fromToMsg: '' }
          this.$store.commit('transactions/setDateFrom', from)
          this.$store.commit('transactions/setDateTo', to)

          if (value === 'all' && this.searchInput === '') {
            this.hasError.searchMsg = this.$t('transactions.filter.searchEmpty')
            let self = this
            self.$nextTick(() => { self.$refs.search.focus() })
            return
          }

          this.getTrasactions(true)
        }
      }
    },
    date () {
      let filter = this.$store.state.transactions.filter
      return {
        from: filter.dateFrom,
        to: filter.dateTo
      }
    },
    isLoading () { return this.$store.state.transactions.loading },
    isListEmpty () { return this.$store.state.transactions.list.length },
    brands () { return this.$store.state.brands },
    brand: {
      get () { return this.$store.state.transactions.filter.brand },
      set (x) {
        this.$store.commit('transactions/setBrand', x)
        this.getTrasactions(true)
      }
    }
  },
  methods: {
    search () {
      this.searchInput = this.searchInput.trim()
      this.$store.commit('transactions/setSearch', this.searchInput)

      if (this.searchInput === '' && this.dateType === 'all') {
        this.hasError.searchMsg = this.$t('transactions.filter.searchEmpty')
        return
      }
      this.hasError.searchMsg = ''

      if ((this.date.from === '' || this.date.to === '') && this.dateType !== 'all') {
        this.hasError.from = this.date.from === ''
        this.hasError.to = this.date.to === ''
        return
      }

      if (this.searchInput !== '') {
        this.$store.commit('transactions/setDate', 'all')
        this.$store.commit('transactions/setDateFrom', '')
        this.$store.commit('transactions/setDateTo', '')
      }
      this.getTrasactions(false)
    },
    blurSearch () {
      let self = this
      if (self.$store.state.transactions.filter.search === self.searchInput.trim()) {
        return
      }

      self.blurDelay = setTimeout(function () {
        self.search()
      }, 500)
    },
    clearSearch () {
      if (this.blurDelay) { clearTimeout(this.blurDelay) }

      if (this.isLoading) { return }

      this.searchInput = ''
      this.$store.commit('transactions/setSearch', this.searchInput)
      this.dateType = 'withinDay'
    },
    calendarFormat (date) { return moment(date).format('MM/DD/YYYY') },
    changeDate (type) {
      this.$store.commit('transactions/setDate', 'custom')

      let format = 'MM/DD/YYYY'
      let from = ''
      let to = ''

      if (type === 'from') {
        this.hasError.from = false
        from = moment(this.date.from)
        this.$store.commit('transactions/setDateFrom', from.format(format))
        if (this.date.to) { to = moment(this.date.to, format) }
      } else {
        this.hasError.to = false
        to = moment(this.date.to)
        this.$store.commit('transactions/setDateTo', to.format(format))
        if (this.date.from) { from = moment(this.date.from, format) }
      }

      this.hasError = { searchMsg: '', from: false, to: false, fromToMsg: '' }

      if (this.date.from === '' || this.date.to === '') {
        if (type === 'from') { this.hasError.to = true }
        else { this.hasError.from = true }
        this.hasError.fromToMsg = this.$t('transactions.filter.emptyDate')
        return
      }
      if (from.format('x') > to.format('x')) {
        this.hasError.from = true
        this.hasError.to = true
        this.hasError.fromToMsg = this.$t('transactions.filter.invalidDateRange')
        return
      }
      this.getTrasactions(true)
    },
    getTrasactions (refreshStats) {
      let self = this

      if (!!self.hasError.fromToMsg || !!self.hasError.searchMsg || self.isLoading) { return }

      self.$store.commit('transactions/setCurrentPage', 0)
      self.$store.commit('transactions/setLoading', true)
      if (!self.searchInput && refreshStats) { self.$store.commit('transactions/setStatsLoading', true) }

      ApiTransactions.getTrasactions(self, { isNext: true }, function (res) {
        self.$store.commit('transactions/setLoading', false)
        if (self.refreshLoading) { self.refreshLoading = false }
        if (!res.error) {
          if (!self.searchInput && refreshStats) {
            self.$store.commit('transactions/setStatsLoading', false)
            self.$store.commit('transactions/setStats', {
              gross: res.stats.revenue,
              credits: res.stats.credits
            })
          }
        }
      })
    },
    refresh () {
      this.refreshLoading = true
      this.getTrasactions(true)
    },
    exportTrx () {
      let self = this
      ApiTransactions.exportTransactions(self, {}, function (res) {
        if (!res.error) {
          console.log(res)
          exporter.downloadCsv(res)
        }
      })
    },
    isNumber (evt) {
      let charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
        evt.preventDefault()
        return
      }
      return true;
    },
    getBrands () {
      let self = this
      if (self.brands.list.length) return
      self.$store.commit('brands/setLoading', true)
      ApiBrands.getBrands(self, function (res) {
        self.$store.commit('brands/setLoading', false)
      })
    },
    brandLabel (dat) {
      let i = CONFIG.brands.findIndex(x => x.id === dat.id)
      if (i == -1) return `(${CONFIG.brands[0].trxCode.toUpperCase()}) ${dat.name}`
      return `(${CONFIG.brands[i].trxCode.toUpperCase()}) ${dat.name}`
    }
  },
  mounted () {
    let self = this

    self.$store.commit('transactions/resetFilters')
    self.getBrands()

    let today = moment()
    self.$store.commit('transactions/setDateFrom', today.format('MM/DD/YYYY'))
    self.$store.commit('transactions/setDateTo', today.format('MM/DD/YYYY'))
  }
}
</script>

<style lang="scss" scoped>
.filter-box{
  display: flex;
  x-justify-content: space-around;
  x-align-items: stretch;
  max-width: 100%;
  margin-bottom: 15px;
  background: #fafafa;
  border:1px solid #aaa;
  border-radius: 2px;
}
.filter{
  width: 100%;
  display: flex;
  height: fit-content;
}

.field-label{
  font-size: 13px;
  color: #999;
  line-height: 1;
  display: block;
  margin-bottom: 4px;
}

.search-box{
  display: inline-block;
  padding: 20px 0 20px 15px;
  position: relative;

  .error-msg{
    position: absolute;
    bottom: 0;
    left: 0;
    width: calc(100% - 15px);
    padding: 2px 5px;
    font-size: 12px;
    text-align: center;
    margin-left: 15px;
    background: #ff6115;
    color: #fff;
    transform-origin: bottom center;
  }
}
.search{
  box-sizing: border-box;
  display: flex;
  width: 330px;

  .search-input-box{
    width: 100%;
    position: relative;

    input{
      display: inline-block;
      vertical-align: middle;
      margin-top: 0;
      border-radius: 5px 0 0 5px;
      padding-right: 30px;

      &.has-error{ border-color:#f33a27; }
    }
    i{
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 5px;
      color: #d92626;
      cursor: pointer;
    }
  }
  button{
    display: inline-block;
    vertical-align: middle;
    line-height: 1;
    border-radius: 0 5px 5px 0;
    padding: 0 20px;
  }
}

.status{
  display: inline-block;
  padding: 20px 15px;
  width: 150px;
}

select{
  padding: 10px;
  height: 46px;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url('/assets/img/dropdown-arrow.png');
  background-repeat: no-repeat;
  background-position: right 50%;

  &[disabled]{ background-color: #ebebe4; }
}

.date-range{
  display: inline-block;
  padding: 20px 20px 25px;
  background: #f3f3f3;
  height: 100%;
  position: relative;

  select{
    width: 150px;
    margin-right: 5px;
  }
  .error-msg{
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 2px 5px;
    font-size: 12px;
    text-align: center;
    background: #ff6115;
    color: #fff;
    transform-origin: bottom center;
  }
}
.datepicker-box{
  display: inline-block;
  width: 110px;
}

.extra{
  padding: 20px 15px;

  .cta{ margin-top: 15px; }
}

.export{
  text-align: right;
  padding: 30px 10px 0;
  width: 130px;
  flex: 0 130px;
}

.slide-up-enter-active, .slide-up-leave-active { transition: all .3s ease; }
.slide-up-enter, .slide-up-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

@media only screen and (max-width: 430px) {
  .filter-box {
    display: block;

    .filter {
      display: block;

      .search-box {
        width: 100%;
        padding: 15px;
        .search {
          width: 100%;
        }
      }

      .status {
        padding: 15px;
        padding-top: 0;
      }

      .date-range {
        display: block;

        select {
          width: 100%;
        }

        .datepicker-box {
          width: 49%;
          margin-top: 10px;
        }
      }

      .extra {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding: 20px;
        padding-top: 10px;
        .status {
          padding: 0;
        } 
        .cta {
          margin-top: 0;
        }
      }
    }

    .export {
      display: none;
    }
    .brand .status{
      width: 100%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Transactions/Filters.vue